import React, { useState } from 'react';
import { TextField, Button, Container, Typography, Box, Link } from '@mui/material';
import { supabase } from '../utils/supabaseClient';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  getAuthorizationFailureReason,
  getDefaultAuthorizedPath,
} from '../auth/authorization';
import { STAFF_PROFILE_SELECT_FIELDS } from '../auth/profileFields';

const LOGIN_PATH = '/login';

// `location.state.from` is navigation context supplied by PrivateRoute, not an
// authorization grant. Only sane local pathnames are accepted here; the
// destination's own PrivateRoute still decides whether this role may view it.
function getSafeReturnPath(candidate) {
  if (typeof candidate !== 'string') return null;
  if (!candidate.startsWith('/')) return null;
  // Protocol-relative ("//host") and backslash ("/\\host") forms can escape the app.
  if (candidate.startsWith('//')) return null;
  if (candidate.startsWith('/\\')) return null;
  if (candidate === LOGIN_PATH) return null;

  return candidate;
}

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    const { error: loginError } = await supabase.auth.signInWithPassword({ email, password });
    if (loginError) {
      setError(loginError.message);
      return;
    }

    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      setError('Unable to verify your authenticated session.');
      await supabase.auth.signOut();
      return;
    }

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select(STAFF_PROFILE_SELECT_FIELDS)
      .eq('id', user.id)
      .maybeSingle();

    if (profileError) {
      console.error('Profile lookup error:', profileError);
      setError('Unable to verify your access profile. Please contact an administrator.');
      await supabase.auth.signOut();
      return;
    }

    const reason = getAuthorizationFailureReason(profile, []);
    if (reason) {
      navigate('/unauthorized', {
        replace: true,
        state: { reason, role: profile?.role ?? null },
      });
      return;
    }

    const returnPath = getSafeReturnPath(location?.state?.from);

    navigate(
      returnPath || getDefaultAuthorizedPath(profile.role) || '/unauthorized',
      { replace: true }
    );
  };


  return (
    <Container maxWidth="xs" sx={{ mt: 8 }}>
      <Box sx={{ boxShadow: 3, p: 4, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom>Login</Typography>
        <form onSubmit={handleLogin}>
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label="Password"
            fullWidth
            margin="normal"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Box sx={{ mt: 1, textAlign: 'right' }}>
            <Link component={RouterLink} to="/forgot-password" underline="hover">
              Forgot password?
            </Link>
          </Box>
          {error && <Typography color="error" variant="body2">{error}</Typography>}
          <Button variant="contained" type="submit" fullWidth sx={{ mt: 2 }}>
            Login
          </Button>
        </form>
      </Box>
    </Container>
  );
};

export default LoginPage;
